/**
 * Keeps MCP OAuth transaction handles and parameters out of logs and Sentry
 * (tmcp U1, Codex r1 G-F1).
 *
 * The consent URL carries `txn` (`/oauth/consent?txn=…`); a signed-out browser
 * reaches it through `/login?redirectUrl=<encoded consent URL>`, and
 * `/oauth/authorize?…` carries `state`, `code_challenge` and the client's
 * redirect. Any string that contains an `/oauth/<endpoint>` path — plain,
 * URL-encoded, or encoded again (a nested `redirectUrl`) — has everything
 * from that path's query / fragment marker onwards replaced by `[Filtered]`.
 * Over-redaction is deliberate: the tail of such a string is never needed to
 * debug anything, and a partial match would leave a txn behind.
 *
 * Framework-free and pure: shared by the Sentry `before*` hooks (client and
 * server) and by the few log lines that print a redirect URL.
 */

/**
 * `/oauth/<segment>[/<segment>…]` (the slashes plain, `%2F` or `%252F`…; the
 * extra segments cover `/api/oauth/bloom/callback?code=…`, FOX-4753) followed by a query
 * or fragment marker (`?`, `#`, or their encodings), then the tail up to
 * whitespace, a quote or an angle bracket.
 */
const OAUTH_URL_TAIL =
  /((?:\/|%(?:25)*2F)oauth(?:\/|%(?:25)*2F)[A-Za-z0-9._~-]*(?:(?:\/|%(?:25)*2F)[A-Za-z0-9._~-]*)*)(?:[?#]|%(?:25)*(?:3F|23))[^\s"'<>`]*/gi;

/**
 * A bare `txn=<opaque handle>` (plain or `%3D`-encoded) outside a URL, e.g. a
 * Sentry `query_string` or `http.query` value. The handle shape matches the
 * server's opaque grant keys (43–128 base64url chars).
 */
const TXN_PARAM = /(?<![A-Za-z])txn(=|%(?:25)*3D)[A-Za-z0-9_-]{43,128}/gi;

export const REDACTED = "[Filtered]";

/**
 * `value` with the query / fragment of every `/oauth/*` URL in it, and every
 * bare `txn=<handle>`, replaced by `[Filtered]`.
 */
export function redactOauthUrls(value: string): string {
  let out = value;
  if (/oauth/i.test(out)) out = out.replace(OAUTH_URL_TAIL, `$1?${REDACTED}`);
  if (/txn/i.test(out)) out = out.replace(TXN_PARAM, `txn$1${REDACTED}`);
  return out;
}

/**
 * A URL reduced to what a log line may carry: origin + pathname (no query, no
 * fragment, no userinfo). A root-relative URL keeps its pathname; anything
 * unparseable becomes a fixed marker.
 */
export function urlForLog(value: string): string {
  try {
    if (value.startsWith("/") && !value.startsWith("//")) {
      return new URL(value, "http://relative.invalid").pathname;
    }
    const url = new URL(value);
    return url.origin === "null" ? `${url.protocol}${url.pathname}` : `${url.origin}${url.pathname}`;
  } catch {
    return "[unparseable]";
  }
}

const MAX_DEPTH = 16;

/**
 * Walks a Sentry event / breadcrumb / recording event and redacts every
 * string in place (and returns it). Plain objects and arrays are walked;
 * cycles and very deep nesting stop the walk.
 */
export function redactOauthUrlsDeep<T>(value: T): T {
  return walk(value, 0, new WeakSet()) as T;
}

function walk(value: unknown, depth: number, seen: WeakSet<object>): unknown {
  if (typeof value === "string") return redactOauthUrls(value);
  if (value === null || typeof value !== "object" || depth > MAX_DEPTH) return value;
  if (seen.has(value)) return value;
  seen.add(value);
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) value[i] = walk(value[i], depth + 1, seen);
    return value;
  }
  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    const next = walk(record[key], depth + 1, seen);
    if (next !== record[key]) {
      try {
        record[key] = next;
      } catch {
        // A read-only property: leave it (nothing we own is frozen).
      }
    }
  }
  return value;
}

/** True when the browser is on an `/oauth/*` page (no replay recording there: the DOM carries the txn). */
export function isOauthPath(pathname: string): boolean {
  return pathname === "/oauth" || pathname.startsWith("/oauth/");
}

/** Decode passes before giving up (a URL still changing after that is treated as an OAuth flow). */
const MAX_DECODE_PASSES = 8;

/**
 * One tolerant percent-decoding pass: every `%XX` becomes its byte as a char.
 * Unlike `decodeURIComponent` it never throws, so a stray `%` elsewhere in
 * the URL cannot stop the scan (only ASCII markers matter here).
 */
function percentDecodeOnce(value: string): string {
  return value.replace(/%([0-9a-f]{2})/gi, (_m, hex: string) =>
    String.fromCharCode(Number.parseInt(hex, 16)),
  );
}

/**
 * The fail-safe markers (Codex r3 R3-F1): `oauth` anywhere, or `txn=` with the
 * `=` plain or still percent-encoded (`%3D`, `%253D`, …).
 */
const OAUTH_FLOW_MARKER = /oauth|txn(?:=|%(?:25)*3d)/;

/**
 * True when a page URL (absolute href, or root-relative path + query +
 * fragment) may belong to an MCP OAuth flow — a deliberately coarse class
 * rule (tmcp U1, Codex r2 R2-F1, r3 R3-F1), not a URL grammar: the URL is
 * percent-decoded repeatedly (up to MAX_DECODE_PASSES, never throwing), each
 * stage with `\` normalised to `/` and lower-cased, and it matches when any
 * stage contains `oauth` or `txn=` anywhere. A URL still changing after the
 * last pass also matches. This catches `/oauth/*` pages, the
 * `/login?redirectUrl=<encoded consent URL>` handoff at any nesting depth,
 * backslash destinations (`/oauth\consent`, `%5C`) the browser normalises,
 * mixed case, and a bare `txn=` in any parameter.
 *
 * Sentry Replay must not record such a page at all: its recorder writes
 * `window.location.href` into a Meta event and snapshots the DOM (the consent
 * form's hidden txn) outside every scrubbing hook. Over-matching is accepted:
 * the AS metadata document, the social-login callbacks (`/api/oauth/google/
 * callback`, …) and any URL that merely mentions `oauth` or `txn=` lose
 * Replay too; that only costs a replay.
 */
export function isOauthFlowUrl(url: string): boolean {
  let current = url;
  for (let pass = 0; pass <= MAX_DECODE_PASSES; pass++) {
    if (OAUTH_FLOW_MARKER.test(current.replace(/\\/g, "/").toLowerCase())) return true;
    const next = percentDecodeOnce(current);
    if (next === current) return false;
    current = next;
  }
  // Still encoded after the bound: fail safe.
  return true;
}

/**
 * Replay DOM rule (tmcp U1, Codex r3 R3-F1): the OAuth consent card root (and
 * its error card) carries `data-oauth-consent` and Sentry's own
 * `data-sentry-block` marker, and `replayIntegration` is given this `block`
 * list. A blocked element is recorded as an empty placeholder of the same
 * size — none of its subtree (the hidden `txn` / `uid` inputs, the user's
 * email) is serialised, whenever the recorder happens to see it. This makes
 * the navigation-timing race moot for DOM content; the page-load rule and the
 * stop-on-navigation guard stay as further layers.
 *
 * `@sentry-internal/replay@10.5.0` (build/npm/esm/index.js) appends its
 * defaults `.sentry-block`, `[data-sentry-block]`, `base`,
 * `iframe[srcdoc]:not([src])` to whatever `block` list is passed
 * (`getOption`, lines 9544–9550; `getPrivacyOptions`, line 9568); the
 * defaults are repeated here so the list reads complete on its own.
 */
export const OAUTH_CONSENT_BLOCK_ATTRIBUTES = {
  "data-oauth-consent": "",
  "data-sentry-block": "",
} as const;

export const OAUTH_REPLAY_BLOCK_SELECTORS: string[] = [
  "[data-oauth-consent]",
  ".sentry-block",
  "[data-sentry-block]",
];

/** Whether Sentry Replay may be installed for a page load at `href` (never for an OAuth flow). */
export function replayAllowedForPageLoad(href: string): boolean {
  return !isOauthFlowUrl(href);
}

/**
 * The Sentry options that scrub OAuth URLs, for both `Sentry.init` calls
 * (entry.client.tsx and entry.server.tsx). `beforeSend` /
 * `beforeSendTransaction` cover error and transaction events (request.url,
 * the Referer header, breadcrumbs, span data, transaction names);
 * `beforeBreadcrumb` scrubs at capture time too.
 */
export const oauthSentryScrubOptions = {
  beforeSend<E>(event: E): E {
    return redactOauthUrlsDeep(event);
  },
  beforeSendTransaction<E>(event: E): E {
    return redactOauthUrlsDeep(event);
  },
  beforeBreadcrumb<B>(breadcrumb: B): B {
    return redactOauthUrlsDeep(breadcrumb);
  },
};

/**
 * Global event processor: also runs for replay events (whose `urls` list the
 * pages visited), which the `before*` hooks do not see.
 */
export function oauthScrubEventProcessor<E>(event: E): E {
  return redactOauthUrlsDeep(event);
}

/**
 * Replay `beforeAddRecordingEvent`: drop every recording event while the
 * browser is on an `/oauth/*` page (its DOM carries the txn as a hidden form
 * value, which URL redaction cannot recognise) and redact the rest.
 */
export function scrubReplayRecordingEvent<E>(event: E, pathname: string): E | null {
  if (isOauthPath(pathname)) return null;
  return redactOauthUrlsDeep(event);
}
